import React from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import { Plus, Minus } from 'lucide-react';
import { cn } from '../../../lib/utils';

export const SectionDisclosure: React.FC<{
  id: string;
  title: string;
  defaultOpen?: boolean;
  className?: string;
  titleClassName?: string;
  children: React.ReactNode;
  flush?: boolean;
  compact?: boolean;
  hideTopBorder?: boolean;
  scrollIntoViewOnExpand?: boolean;
}>
  = ({
    id,
    title,
    defaultOpen = true,
    className,
    titleClassName,
    children,
    flush = false,
    compact = false,
    hideTopBorder = false,
    scrollIntoViewOnExpand = false,
  }) => {
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(`panel.section.${id}`);
      return raw != null ? JSON.parse(raw) : defaultOpen;
    } catch { return defaultOpen; }
  });

  const handleChange = React.useCallback((o: boolean) => {
    setOpen(o);
    try {
      localStorage.setItem(`panel.section.${id}`, JSON.stringify(o));
    } catch {
      // localStorage can be unavailable in private or server-like environments.
    }
  }, [id]);

  React.useEffect(() => {
    if (scrollIntoViewOnExpand && open && sectionRef.current) {
      const timer = setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [scrollIntoViewOnExpand, open]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ id: string; open?: boolean }>).detail;
      if (!detail || detail.id !== id) return;
      handleChange(detail.open ?? true);
    };
    window.addEventListener('panel:toggle', handler as EventListener);
    return () => window.removeEventListener('panel:toggle', handler as EventListener);
  }, [id, handleChange]);

  return (
    <Collapsible open={open} onOpenChange={handleChange} data-section-id={id}>
      <div ref={sectionRef} className={cn(className)}>
        {!hideTopBorder && <div className="h-px bg-border" />}
        <div className={cn(flush ? 'px-0' : 'px-4')}>
          <CollapsibleTrigger
            className={cn(
              'flex w-full items-center justify-between text-left outline-none focus-visible:outline-none focus-visible:ring-0 group',
              compact ? 'py-2.5' : 'py-3',
            )}
            data-section-id={id}
            data-testid={`tid.genogram.section.${id}`}
          >
            <h3 className={cn('text-[11px] font-semibold text-foreground uppercase tracking-wider', titleClassName)}>
              {title}
            </h3>
            <div className="opacity-70 group-hover:opacity-100 transition-opacity">
              {open ? (
                <Minus className="h-4 w-4 text-foreground flex-shrink-0" />
              ) : (
                <Plus className="h-4 w-4 text-foreground flex-shrink-0" />
              )}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className={compact ? 'pb-1' : 'pb-2'}>
              {children}
            </div>
          </CollapsibleContent>
        </div>
      </div>
    </Collapsible>
  );
};
